import { useState, useEffect, useRef } from "react";
import {
  Package, Wifi, WifiOff, CloudOff, RefreshCw, Check,
} from "lucide-react";
import { AccountButton } from "./AccountButton";
import { queueCount, flushQueue } from "@/lib/syncQueue";
import { toast } from "sonner";

export function AppHeader({ user, isOnline, pendingSync, slPending, syncWithServer, handleLogout, openCreateForm, onManualSync, onUserUpdate, onStartTutorial, onShowPrivacy, onShowTerms, onShowMonthlyHistory, activePage, setActivePage }) {
  const [syncing, setSyncing] = useState(false);
  const [queued, setQueued] = useState(queueCount());
  const [queueSyncing, setQueueSyncing] = useState(false);
  const [justSynced, setJustSynced] = useState(false);
  const totalPending = (pendingSync || 0) + (slPending || 0) + queued;
  const totalPendingRef = useRef(totalPending);
  useEffect(() => { totalPendingRef.current = totalPending; });

  useEffect(() => {
    const onChange = (e) => setQueued(e.detail?.count ?? queueCount());
    const onSyncing = () => setQueueSyncing(true);
    const onFlushed = (e) => {
      setQueueSyncing(false);
      setQueued(e.detail?.remaining ?? queueCount());
      if ((e.detail?.flushed || 0) > 0 && (e.detail?.remaining || 0) === 0) {
        setJustSynced(true);
        setTimeout(() => setJustSynced(false), 2500);
      }
    };
    window.addEventListener("sync-queue-changed", onChange);
    window.addEventListener("sync-queue-syncing", onSyncing);
    window.addEventListener("sync-queue-flushed", onFlushed);
    return () => {
      window.removeEventListener("sync-queue-changed", onChange);
      window.removeEventListener("sync-queue-syncing", onSyncing);
      window.removeEventListener("sync-queue-flushed", onFlushed);
    };
  }, []);

  const handleSync = async () => {
    setSyncing(true);
    if (!navigator.onLine) {
      toast.error("You're offline — changes will sync when you're back online");
      setTimeout(() => setSyncing(false), 600);
      return;
    }
    try { await Promise.all([onManualSync(), flushQueue()]); } catch {}
    // Let all pending counters settle so the toast matches the badge exactly
    await new Promise(r => setTimeout(r, 700));
    const still = Math.max(queueCount(), totalPendingRef.current);
    if (still > 0) toast.warning(`${still} change${still === 1 ? '' : 's'} still waiting to sync`);
    else toast.success("Synced!");
    setTimeout(() => setSyncing(false), 600);
  };

  const pages = [
    { id: 'calculator', label: 'Calculator' },
    { id: 'cutlist', label: 'Cut List' },
    { id: 'lumber', label: 'Lumber' },
    { id: 'orders', label: 'Orders' },
  ];

  return (
    <div className="sticky top-0 z-10">
    <header className="bg-zinc-900" data-testid="app-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14">
          <div className="flex items-center gap-2.5 min-w-0 flex-1">
            <Package className="w-6 h-6 text-orange-500 flex-shrink-0" strokeWidth={2.5} />
            <h1
              className="text-lg font-bold text-white tracking-tight truncate min-w-0"
              style={{ fontFamily: "Chivo, sans-serif" }}
            >
              Pallet Cut Calculator
            </h1>
            <div className={`flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider flex-shrink-0 ${
              isOnline ? 'bg-green-900/50 text-green-400' : 'bg-amber-900/50 text-amber-400'
            }`} data-testid="online-status-badge">
              {isOnline ? (
                <><Wifi className="w-2.5 h-2.5" />Online</>
              ) : (
                <><WifiOff className="w-2.5 h-2.5" />Offline</>
              )}
            </div>
            {totalPending > 0 ? (
              <button onClick={handleSync} disabled={syncing} title={`${totalPending} waiting to sync — tap to sync now`}
                className="flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold bg-blue-900/50 text-blue-400 hover:bg-blue-900/80 transition-colors flex-shrink-0"
                data-testid="sync-pending-badge">
                <CloudOff className="w-2.5 h-2.5" />
                {totalPending}
              </button>
            ) : justSynced ? (
              <div className="flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold bg-green-900/50 text-green-400 flex-shrink-0" data-testid="sync-done-badge">
                <Check className="w-2.5 h-2.5" />Synced
              </div>
            ) : null}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <span className="text-xs text-zinc-500 hidden sm:inline mr-2">{user.email}</span>
            <button
              onClick={handleSync}
              disabled={syncing}
              className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors rounded-sm"
              data-testid="sync-btn"
              title="Sync data"
            >
              <RefreshCw className={`w-4 h-4 ${(syncing || queueSyncing) ? 'animate-spin' : ''}`} />
            </button>
            <AccountButton user={user} handleLogout={handleLogout} onUserUpdate={onUserUpdate} onStartTutorial={onStartTutorial} onShowPrivacy={onShowPrivacy} onShowTerms={onShowTerms} onShowMonthlyHistory={onShowMonthlyHistory} />
          </div>
        </div>
      </div>
    </header>
    <nav className="bg-zinc-800 border-t border-zinc-700">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex">
        {pages.map(p => (
          <button
            key={p.id}
            onClick={() => setActivePage(p.id)}
            className={`px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${
              activePage === p.id
                ? 'bg-orange-600 text-white'
                : 'text-zinc-400 hover:text-white hover:bg-zinc-700'
            }`}
            data-testid={`nav-${p.id}`}
          >
            {p.label}
          </button>
        ))}
      </div>
    </nav>
    </div>
  );
}
